interface FocusedComponent {
    element: HTMLElement;
}

class FocusManager {
    private focusedComponents = new Map<string, FocusedComponent>();
    private cachedFocusedComponentId: string | null = null;
    private mutationObserver: MutationObserver | null = null;

    setFocus(componentId: string, element: HTMLElement): void {
        this.focusedComponents.clear();

        this.focusedComponents.set(componentId, {
            element,
        });

        this.cachedFocusedComponentId = componentId;

        this.setupAutoCleanup();
    }

    unfocus(componentId: string): void {
        this.focusedComponents.delete(componentId);

        if (this.cachedFocusedComponentId === componentId) {
            this.cachedFocusedComponentId = null;
        }
    }

    unregister(componentId: string): void {
        this.focusedComponents.delete(componentId);

        if (this.cachedFocusedComponentId === componentId) {
            this.cachedFocusedComponentId = null;
        }
    }

    clearFocus(): void {
        this.focusedComponents.clear();
        this.cachedFocusedComponentId = null;
    }

    isFocused(componentId: string): boolean {
        return this.focusedComponents.has(componentId);
    }

    getFocusedComponentId(): string | null {
        if (this.cachedFocusedComponentId) {
            return this.cachedFocusedComponentId;
        }
        if (this.focusedComponents.size === 0) return null;

        return this.focusedComponents.keys().next().value || null;
    }

    private setupAutoCleanup(): void {
        if (!this.mutationObserver) {
            this.mutationObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    mutation.removedNodes.forEach(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const removedElement = node as Element;
                            this.focusedComponents.forEach((focusedComponent, id) => {
                                if (
                                    removedElement === focusedComponent.element ||
                                    removedElement.contains(focusedComponent.element)
                                ) {
                                    this.unregister(id);
                                }
                            });
                        }
                    });
                });
            });

            this.mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }
    }

    public destroy(): void {
        this.mutationObserver?.disconnect();
        this.mutationObserver = null;
        this.focusedComponents.clear();
        this.cachedFocusedComponentId = null;
    }
}

export const focusManager = new FocusManager();
